import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  IconButton,
  Slide,
  Paper,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  confirmCookies,
  getCookiePreferences
} from 'api';

// Categories of cookies to manage
const cookieCategories = [
  {
    id: 'essential',
    name: 'Essential',
    description: 'These cookies are necessary for the website to function and cannot be switched off.',
    required: true
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'These cookies allow us to count visits and track sources.',
    required: false
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'These cookies are used to track visitors across websites.',
    required: false
  }
];

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  useEffect(() => {
    window.openCookiePreferences = () => setShowPreferences(true);
    
    return () => {
      delete window.openCookiePreferences;
    };
  }, []);
  
  useEffect(() => {
    // Check if consent has been given before
    const savedConsent = localStorage.getItem('cookieConsent');
    if (!savedConsent) {
      setShowBanner(true);
    } else {
      setPreferences(JSON.parse(savedConsent));
    }
  }, []);

  useEffect(() => {
    // Function to fetch preferences from server
    const fetchPreferencesFromServer = async () => {
      try {
        const response = await getCookiePreferences();
        if (response.ok) {
          const data = await response.json();
          setPreferences(data);
          localStorage.setItem('cookieConsent', JSON.stringify(data));
          setShowBanner(false);
        }
      } catch (error) {
        console.error('Error fetching cookie preferences:', error);
        
        // Fallback to local storage
        const savedConsent = localStorage.getItem('cookieConsent');
        if (savedConsent) {
          setPreferences(JSON.parse(savedConsent));
        } else {
          setShowBanner(true);
        }
      }
    };
  
    // Try to get preferences from server first
    fetchPreferencesFromServer();
  }, []);

  const savePreferencesToServer = async (prefs) => {
    try {
      await confirmCookies(prefs);
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowPreferences(false);
    
    // Apply cookie preferences
    applyPreferences(allAccepted);

    savePreferencesToServer(allAccepted);
  };

  const handleAcceptEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false
    };
    setPreferences(essentialOnly);
    localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly));
    setShowBanner(false);
    
    // Apply cookie preferences
    applyPreferences(essentialOnly);

    savePreferencesToServer(essentialOnly);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowPreferences(false);
    
    // Apply cookie preferences
    applyPreferences(preferences);

    savePreferencesToServer(preferences);
  };

  const handleChange = (event) => {
    const { name, checked } = event.target;
    setPreferences({
      ...preferences,
      [name]: checked
    });
  };
  
  // Function to apply preferences (enable/disable cookies)
  const applyPreferences = (prefs) => {
    // Example implementation - customize based on your needs
    if (!prefs.analytics) {
      // Disable analytics cookies
      document.cookie.split(";").forEach(cookie => {
        if (cookie.includes('_ga') || cookie.includes('_gid')) {
          const name = cookie.split("=")[0].trim();
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });
    }
    
    if (!prefs.marketing) {
      // Disable marketing cookies
      document.cookie.split(";").forEach(cookie => {
        if (cookie.includes('ads') || cookie.includes('__gads')) {
          const name = cookie.split("=")[0].trim();
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });
    }
  };
  
  // Function to reset cookie consent for testing
  const resetConsent = () => {
    localStorage.removeItem('cookieConsent');
    setShowBanner(true);
    setPreferences({
      essential: true,
      analytics: false,
      marketing: false
    });
  };

  return (
    <>
      {/* Bottom Banner */}
      <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            zIndex: 9999,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2">
              This site uses cookies to improve your experience. By continuing to use this site, you agree to our use of cookies.
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => {
                setShowPreferences(true);
                // Hide banner when preferences dialog is open on mobile
                if (isMobile) {
                  setShowBanner(false);
                }
              }}
              startIcon={<SettingsIcon />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Cookie Settings
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleAcceptEssential}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Essential Only
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleAcceptAll}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Accept All
            </Button>
          </Box>
        </Paper>
      </Slide>
      
      {/* Cookie Settings Dialog */}
      <Dialog 
        open={showPreferences} 
        onClose={() => {
          setShowPreferences(false);
          // Show banner again on mobile if no consent has been saved
          if (isMobile && !localStorage.getItem('cookieConsent')) {
            setShowBanner(true);
          }
        }}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            m: isMobile ? 2 : 'auto', // Add margin on mobile
            maxHeight: isMobile ? 'calc(100% - 64px)' : 'auto', // Ensure it's not too tall on mobile
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 2
        }}>
          Cookie Preferences
          <IconButton 
            onClick={() => {
              setShowPreferences(false);
              // Show banner again on mobile if no consent has been saved
              if (isMobile && !localStorage.getItem('cookieConsent')) {
                setShowBanner(true);
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, pt: 0 }}>
          <Typography variant="body2" sx={{ mb: 3 }}>
            We use cookies to improve your experience on our site. Some are essential for the
            site to function, while others help us understand how you use the site.
          </Typography>
          
          {cookieCategories.map((category) => (
            <Box key={category.id} sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={preferences[category.id]}
                    onChange={handleChange}
                    name={category.id}
                    disabled={category.required}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle1">{category.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.description}
                      {category.required && ' (Required)'}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
              <Divider sx={{ mt: 1 }} />
            </Box>
          ))}
        </DialogContent>
        
        <DialogActions 
          sx={{ 
            px: 3, 
            pb: 3, 
            pt: 1,
            justifyContent: isMobile ? 'center' : 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0
          }}
        >
          <Button 
            onClick={handleSavePreferences}
            variant="outlined"
            fullWidth={isMobile}
          >
            Save Preferences
          </Button>
          <Button 
            onClick={handleAcceptAll} 
            variant="contained" 
            color="primary"
            fullWidth={isMobile}
          >
            Accept All
          </Button>
        </DialogActions>
      </Dialog>

      
      {/* Hidden button for development to reset consent */}
      {process.env.NODE_ENV !== 'production' && (
        <Button
          onClick={resetConsent}
          variant="text"
          size="small"
          color="error"
          sx={{ 
            position: 'fixed', 
            bottom: 20, 
            right: 20,
            zIndex: 1000,
            fontSize: '0.75rem'
          }}
        >
          Reset Cookie Consent
        </Button>
      )}
    </>
  );
};

export default CookieConsent;